import React, { useState } from 'react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { formatApiErrorDetail } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PlanCard = ({ plan, user, paypalReady, featured, perks, onSuccess }) => {
  const navigate = useNavigate();
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');

  const createOrder = async () => {
    setError('');
    const { data } = await axios.post(`${API}/payments/paypal/create-order`, { plan_id: plan.id }, { withCredentials: true });
    return data.order_id;
  };

  const onApprove = async (data) => {
    setCapturing(true);
    try {
      const res = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.orderID }, { withCredentials: true });
      onSuccess(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Payment could not be confirmed');
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div
      className={`relative rounded-3xl p-7 flex flex-col border shadow-sm transition-transform hover:-translate-y-1 ${
        featured ? 'bg-amber-950 text-amber-50 border-amber-900 shadow-xl' : 'bg-white/70 backdrop-blur-sm border-amber-200 text-amber-950'
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {featured && (
        <span className="absolute -top-3 left-7 bg-orange-500 text-white text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full">Best value</span>
      )}
      <p className={`text-sm font-medium tracking-wider uppercase ${featured ? 'text-amber-300' : 'text-amber-700'}`}>{plan.name}</p>
      <div className="mt-3 flex items-end gap-2">
        <span className="font-display text-5xl font-black" data-testid={`plan-price-${plan.id}`}>${plan.price.replace('.00', '')}</span>
        <span className={`mb-2 text-sm ${featured ? 'text-amber-200/80' : 'text-amber-800/70'}`}>one‑time · ${plan.per_month}/mo</span>
      </div>
      <ul className="mt-6 space-y-2 text-sm flex-1">
        {perks.map((p) => (
          <li key={p} className="flex gap-2">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? 'text-orange-400' : 'text-emerald-600'}`} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-7">
        {!user ? (
          <Button onClick={() => navigate('/register')} className={`w-full rounded-full ${featured ? 'bg-orange-500 hover:bg-orange-400 text-white' : 'bg-amber-900 hover:bg-amber-800 text-amber-50'}`} data-testid={`plan-signup-${plan.id}`}>
            Create free account
          </Button>
        ) : !paypalReady ? (
          <Button disabled className="w-full rounded-full" data-testid={`plan-unavailable-${plan.id}`}>Payments opening soon</Button>
        ) : capturing ? (
          <div className="flex items-center justify-center gap-2 py-3 text-sm" data-testid={`plan-capturing-${plan.id}`}>
            <Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…
          </div>
        ) : (
          <div data-testid={`paypal-buttons-${plan.id}`}>
            <PayPalButtons
              style={{ layout: 'vertical', shape: 'pill', color: featured ? 'gold' : 'black', label: 'pay', height: 45 }}
              createOrder={createOrder}
              onApprove={onApprove}
              onError={() => setError('PayPal could not start checkout. Please try again.')}
            />
          </div>
        )}
        {error && <p className="mt-3 text-sm text-rose-500" data-testid={`plan-error-${plan.id}`}>{error}</p>}
      </div>
    </div>
  );
};
